import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import { colors, typography } from 'settings';
import { SolidButton } from 'libs/core-components/SolidButton/SolidButton/SolidButton';
import Link from 'common/Link/BaseLink';
import linkTo from 'linkTo';
import Box from 'libs/core-components/Box/Box';
import Stack from 'libs/core-components/Stack/Stack';
import redirectTo from 'common/Ssr/redirectTo';
import { FormattedReactMessage, FormattedMessage } from '@99designs/common/i18n';
import getCsrfToken from '@99designs/common/utils/app/getCsrfToken';
import Bugsnag from '@bugsnag/js';
import is99StaffEmail from './common/is99StaffEmail';
import { Layout } from './common/Layout';
import { Inline } from 'libs/core-components/Inline/Inline';
import ProgressBar from 'common/ProgressBar';
import { FieldMessage } from 'pages/account/components/Form/Field';
import loadable from '@loadable/component';
import { PASSWORD_MIN, PASSWORD_MIN_ADMIN } from '../../common/constants';
import { useForm } from 'react-hook-form';
import BaseRouterLink from 'common/Link/BaseRouterLink';
import Loading from 'common/Loading';
import { useAppConfig } from 'common/AppConfigContext';
import TextField from './TextField';
import { getErrorForField, setBackendErrors } from './common/utils';
import fetchPreAuthorisation from './fetchPreAuthorisation';
import Captcha from './Captcha';

const PasswordStrengthBar = loadable(
    () => import('../account/components/PasswordPage/PasswordStrengthBar/PasswordStrengthBar')
);

type RoleType = 'client' | 'designer';

export interface SignupContainerProps {
    email?: string;
    variant?: 'designer' | 'invite';
}

interface SignUpProps {
    email?: string;
    hideRoleSelector?: boolean;
    showRecaptcha?: boolean;
    source?: string;
    userRole?: RoleType;
}

interface FormState {
    email: string;
    password: string;
    radio_user: string;
    displayname: string;
    confirmPassword: string;
}

const PuntRoadSubtleText = styled.small`
    color: ${colors.puntRoad};
    font-size: ${typography.typeScale.size2}px;
`;

const RadioInput = styled.input`
    /* Increase specificity */
    && {
        width: 16px;
        height: 16px;
    }
`;

function SignUpContainer(props: SignupContainerProps) {
    const { search } = useLocation();
    const [email, setEmail] = useState<string>(props.email || '');
    const [loading, setLoading] = useState<boolean>(true);
    const [showRecaptcha, setShowRecaptcha] = useState<boolean>(true);
    const [source, setSource] = useState<string>('');

    useEffect(() => {
        fetchPreAuthorisation(search).then((data) => {
            if (data.target) {
                redirectTo(data.target);
                return;
            } else {
                if (data.skip_recaptcha) {
                    setShowRecaptcha(false);
                }
                if (data.email) {
                    setEmail(data.email);
                }
                if (data.source) {
                    setSource(data.source);
                }
            }

            setLoading(false);
        });
    }, []);

    const passedProps = {
        email,
        showRecaptcha,
        source,
    };

    if (loading) {
        return <Loading />;
    }

    if (props.variant === 'invite') {
        return <SignUp hideRoleSelector userRole="designer" {...passedProps} />;
    } else if (props.variant === 'designer') {
        return <SignUp userRole="designer" {...passedProps} />;
    }

    return <SignUp {...passedProps} />;
}

export function SignUp({
    email = '',
    hideRoleSelector = false,
    showRecaptcha = true,
    source = '',
    userRole = 'client',
}: SignUpProps) {
    const { search } = useLocation();
    const { recaptchaSiteKey } = useAppConfig();

    const [recaptchaError, setRecaptchaError] = useState('');

    const recaptchaRef = useRef<any>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const {
        register,
        handleSubmit,
        watch,
        errors,
        setError,
        formState: { isSubmitting },
    } = useForm({
        defaultValues: {
            email,
            password: '',
            confirmPassword: '',
            radio_user: userRole,
        },
    });
    const currentPassword = watch('password');
    const currentEmail = watch('email');
    const currentRole = watch('radio_user');

    function handleRecaptchaChange(token: string) {
        setRecaptchaError('');
        setRecaptchaToken(token);
    }

    function onSubmit({ email, password, radio_user, displayname }: FormState) {
        const needsRecaptcha = showRecaptcha && !recaptchaToken;
        if (recaptchaSiteKey && needsRecaptcha) {
            setRecaptchaError(__("Confirm that you're not a robot"));
            return;
        }

        const params = new URLSearchParams(search);
        const paramString = params.toString();
        const url = new URL(
            `/accounts/xhr/signup${paramString ? `?${paramString}` : ''}`,
            window.location.origin
        );

        fetch(url.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
                email,
                password,
                role: radio_user || currentRole,
                displayName: displayname,
                source,
                csrf_token: getCsrfToken(),
                recaptchaToken,
            }),
        })
            .then((response) => response.json().then((d) => ({ status: response.status, body: d })))
            .then((d) => {
                if (d.status === 200) {
                    if (d.body.target) {
                        window.location.href = d.body.target;
                    }
                    return;
                }

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                if (d.body.errors) {
                    //todo will also have to do something with errors not associated with a form field..
                    setBackendErrors(d.body.errors, setError);
                    return;
                }
            })
            .catch((e) => {
                Bugsnag.notify(e, (event) => {
                    event.context = 'Signing up';
                });
            });
    }

    return (
        <Layout
            title={__('Sign up')}
            description={__('Sign up for a 99designs account')}
            heading={__('Sign up')}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                {!hideRoleSelector && (
                    <div className="field">
                        <Stack space="quarterGutter">
                            <Inline space="halfGutter">
                                <RadioInput
                                    className="input input-radio input--inline"
                                    id="radio_client"
                                    name="radio_user"
                                    type="radio"
                                    value="client"
                                    defaultChecked={currentRole === 'client'}
                                    data-testid="signup-client"
                                    ref={register}
                                />
                                <label
                                    className="field__title field__title--no-margin-bottom"
                                    htmlFor="radio_client"
                                >
                                    <span>{__('I need something designed')}</span>
                                </label>
                            </Inline>
                            <Inline space="halfGutter">
                                <RadioInput
                                    className="input input-radio input--inline"
                                    id="radio_designer"
                                    name="radio_user"
                                    type="radio"
                                    value="designer"
                                    defaultChecked={currentRole === 'designer'}
                                    data-testid="signup-designer"
                                    ref={register}
                                />
                                <label
                                    className="field__title field__title--no-margin-bottom"
                                    htmlFor="radio_designer"
                                >
                                    <span> {__("I'm a designer")} </span>
                                </label>
                            </Inline>
                        </Stack>
                    </div>
                )}

                <Box paddingBottom="space8">
                    <TextField
                        key="email"
                        error={getErrorForField('email', errors)}
                        name="email"
                        type="text"
                        label={__('Email')}
                        ref={register({
                            required: __('Enter your email address'),
                        })}
                        data-testid="signup-email"
                        autoFocus
                        autoComplete="username"
                    />
                </Box>

                {currentRole === 'designer' && (
                    <Box paddingBottom="space8">
                        <TextField
                            key="displayname"
                            error={getErrorForField('displayname', errors)}
                            name="displayname"
                            type="text"
                            label={__('Display name')}
                            data-testid="signup-designer-display-name"
                            ref={register}
                        />
                    </Box>
                )}

                <Box paddingBottom="space8">
                    <Stack space="quarterGutter">
                        <TextField
                            key="password"
                            error={getErrorForField('password', errors)}
                            name="password"
                            type="password"
                            label={__('Password')}
                            data-testid="signup-password"
                            ref={register({
                                required: __('Enter your password'),
                            })}
                            autoComplete="new-password"
                        />

                        <PasswordStrengthBar
                            password={currentPassword}
                            fallback={<ProgressBar progress={0} size={'small'} variant={'soft'} />}
                        />
                        <FieldMessage>
                            <PuntRoadSubtleText>
                                <FormattedMessage
                                    message={__(
                                        'Use {minChars} or more characters with a mix of letters, numbers and symbols'
                                    )}
                                    values={{
                                        minChars: is99StaffEmail(currentEmail)
                                            ? PASSWORD_MIN_ADMIN
                                            : PASSWORD_MIN,
                                    }}
                                />
                            </PuntRoadSubtleText>
                        </FieldMessage>
                    </Stack>
                </Box>

                {showRecaptcha && !!recaptchaSiteKey && (
                    <Box paddingBottom="space8">
                        <Captcha
                            ref={recaptchaRef}
                            siteKey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                            error={recaptchaError || getErrorForField('captcha', errors)}
                        />
                    </Box>
                )}

                <Box paddingBottom="space8">
                    <PuntRoadSubtleText>
                        <FormattedReactMessage
                            message={__(
                                'By signing up, you agree to our <Link1>Terms of Service</Link1> and have read and acknowledge the <Link2>Privacy Policy</Link2>'
                            )}
                            replacements={{
                                Link1: <Link href={linkTo.termsOfUse()} size="subtle" />,
                                Link2: <Link href={linkTo.privacyPolicy()} size="subtle" />,
                            }}
                        />
                    </PuntRoadSubtleText>
                </Box>

                <SolidButton
                    variant="primary"
                    width="fill"
                    size="large"
                    processing={isSubmitting}
                    data-testid="signup-button"
                >
                    {__('Sign up')}
                </SolidButton>
            </form>
            <p>
                {__('Already have an account?')}{' '}
                <BaseRouterLink to={linkTo.login()}>{__('Log in')}</BaseRouterLink>
            </p>
        </Layout>
    );
}

export default SignUpContainer;
